import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import stripeConfig, { resolveStripePublishableKey } from '@/config/stripe.config';
import useLimitedEditionShippingQuote from '@/hooks/useLimitedEditionShippingQuote';
import usePaymentProfile from '@/hooks/usePaymentProfile';
import useTranslations from '@/i18n/useTranslations';
import useArtworkPurchasePayment from '@/pages/artwork/hooks/useArtworkPurchasePayment';
import {
  getArtworkPurchaseDetails,
  getArtworkPurchaseGalleryProfile,
  getArtworkShippingConfig,
} from '@/pages/artwork/utils/artworkView.selectors';
import {
  ARTWORK_PREFERENCE_SIGNALS,
  trackArtworkPreferenceInteraction,
} from '@/services/artworkPreferenceInteraction.service';
import UserService from '@/services/user.service';
import PurchasePaymentMethodPanel from '@/shared-components/artwork/buy-details/payment-methods/PurchasePaymentMethodPanel';
import {
  FRAME_OPTION_LABELS,
  MUSEUM_LABEL_OPTION_LABELS,
} from '@/shared-components/artwork/buy-details/select-size/constants';
import PersonalizedMessage from '@/shared-components/artwork/buy-details/shipping-details/components/PersonalizedMessage';
import ArtworkLabel from '@/shared-components/artwork/components/ArtworkLabel';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import FormError from '@/shared-components/error/components/FormError';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import PurchasePaymentModal from '@/shared-components/payment/PurchasePaymentModal';
import BillingDetailsCard from '@/shared-components/payment-profile/BillingDetailsCard';
import { formatCurrencyAmount } from '@/utils/currency.utils';
import { resolveLocalizedPublicPath } from '@/utils/localePublicUrl';
import NumberUtils from '@/utils/number.utils';
import {
  calculateVatAmount,
  qualifiesForEuReverseCharge,
  resolveOriginalTaxContext,
  resolveVatRate,
  TAX_MODES,
} from '@/utils/vat.utils';

// Local wrappers/stubs to avoid duplication and keep imports simple
import PaymentDetails from './components/PaymentDetails';
import PopupCertificateOfAuthenticity from './components/popup-certificate-of-authenticity';
import PopupCertificateOfOwnership from './components/popup-certificate-of-ownership';
import SelectSize from './select-size';
import ShippingDetails from './shipping-details';

import './purchase-process.css';
const PROFILE_ERROR_CODES = new Set([
  "BIDDER_PROFILE_INCOMPLETE",
  "MISSING_CARDHOLDER_NAME",
  "BIDDER_SHIPPING_REQUIRED",
]);
const ZERO_VAT_TOOLTIP = "VAT is 0% for this destination. Customs duties or import taxes may still be charged separately to the collector.";
const TAX_NOTICE_COPY = Object.freeze({
  LOCAL_TAX_MAY_APPLY_SELLER_RESPONSIBLE:
    "Taxes may apply. The seller is responsible for any applicable local taxes.",
  IMPORT_DUTIES_MAY_APPLY_ON_DELIVERY:
    "Import duties and taxes may apply on delivery.",
  TAX_COLLECTED_ON_BEHALF_OF_SELLER:
    "Tax included (collected on behalf of the seller).",
  REVERSE_CHARGE_EU_VAT:
    "VAT not charged. Reverse charge applies.",
  SELLER_RESPONSIBLE_TAX_NOT_COLLECTED:
    "Taxes may apply. This seller will handle any applicable taxes.",
});

function roundMoney(value) {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return Number(numeric.toFixed(2));
}

function formatMoney(value, currency) {
  const rounded = roundMoney(value);
  if (!Number.isFinite(rounded)) {
    return null;
  }
  if (Number.isInteger(rounded)) {
    return NumberUtils.getArtworkPrice(rounded, currency);
  }
  return formatCurrencyAmount(rounded, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) || null;
}

function normalizePriceValue(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function parseVatRate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return numeric > 0 ? numeric : null;
}

function formatVatRate(rate) {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {
    return null;
  }
  const percentage = rate * 100;
  const formatted = Number.isInteger(percentage)
    ? percentage.toString()
    : percentage.toFixed(2).replace(/\.?0+$/, '');
  return `${formatted}%`;
}

export default function BuyDetails() {
  const t = useTranslations('common.shared.artwork_view');
  const { state } = useLocation();
  const navigate = useNavigate();
  const { artworkData, slug, typeOfPurchase = 'standard' } = state || {};
  const initialPurchaseResumeState = state?.artworkPurchaseResume ?? null;
  const limitedEditionSelection = state?.limitedEditionSelection ?? null;
  const [pendingLimitedEditionSelection, setPendingLimitedEditionSelection] = useState(limitedEditionSelection);
  const isLimitedEditionPurchase = typeOfPurchase === 'limited_edition';
  useEffect(() => {
    setPendingLimitedEditionSelection(limitedEditionSelection);
  }, [limitedEditionSelection]);

  useEffect(() => {
    if (!isLimitedEditionPurchase || !pendingLimitedEditionSelection) {
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      limited_edition_size: {
        size: pendingLimitedEditionSelection.sizeKey,
        size_data: pendingLimitedEditionSelection.sizeData,
      },
    }));
    setPendingLimitedEditionSelection(null);
  }, [isLimitedEditionPurchase, pendingLimitedEditionSelection]);

  const fallbackRoute = slug ? `/artwork/${slug}` : '/';
  const goBackHandler = () => {
    if (isLimitedEditionPurchase && currentBuySection === 'shipping') {
      setCurrentBuySection('size');
      return;
    }

    const canNavigateBack = typeof window !== 'undefined' && window.history.length > 1;
    if (canNavigateBack) {
      navigate(-1);
      return;
    }

    navigate(fallbackRoute, { replace: true });
  };
  const [currentBuySection, setCurrentBuySection] = useState(
    initialPurchaseResumeState?.currentBuySection ?? (isLimitedEditionPurchase ? 'size' : 'shipping')
  );

  const [formState, setFormState] = useState(initialPurchaseResumeState?.formState ?? {
    limited_edition_size: null,
    limited_edition_frame: null,
    limited_edition_frame_price: 0,
    limited_edition_museum_label: null,
    limited_edition_museum_label_price: 0,
    shipping_details: null,
    billing_address: null,
    billing_info: null,
    personalized_message: '',
  });
  const handleManagePaymentMethods = useCallback(() => {
    navigate('/profile/settings/payment-methods', {
      state: {
        checkoutReturn: {
          to: '/artwork/purchase',
          state: {
            ...state,
            artworkPurchaseResume: {
              currentBuySection,
              formState,
            },
          },
        },
      },
    });
  }, [currentBuySection, formState, navigate, state]);

  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [ownershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [serverPricing, setServerPricing] = useState(null);
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const profileBillingAddress = paymentProfile?.profile?.billing_address ?? null;
  const profileBillingInfo = paymentProfile?.profile?.billing_info ?? null;
  const stripeInstanceRef = useRef(null);

  const resolveStripeInstance = useCallback(async () => {
    if (stripeInstanceRef.current) {
      return stripeInstanceRef.current;
    }
    const publishableKey = stripeConfig.publishableKey || await resolveStripePublishableKey();
    if (!publishableKey) {
      return null;
    }
    const instance = await loadStripe(publishableKey);
    stripeInstanceRef.current = instance;
    return instance;
  }, []);
  const {
    completePurchasePayment,
    dismissPurchasePayment,
    handlePaymentIntentResponse,
    purchasePayment,
  } = useArtworkPurchasePayment({ navigate, resolveStripeInstance });

  const promptProfileUpdate = useCallback(async (errorCode) => {
    setFormError({ element: 'purchase_error', error_code: errorCode });
    if (typeof ensurePaymentProfile !== "function") {
      return;
    }
    try {
      await ensurePaymentProfile({ forceSetup: true });
      setFormError(null);
    }
    catch (error) {
      if (error?.code === "BIDDER_PROFILE_CANCELLED") {
        return;
      }
      console.warn("[PurchaseProcess] Unable to open payment profile modal", error);
    }
  }, [ensurePaymentProfile]);

  const limitedEditionSizeKey = formState.limited_edition_size?.size ?? null;
  const shippingCountry = formState.shipping_details?.address_country ?? null;
  const {
    quote: limitedEditionShippingQuote,
  } = useLimitedEditionShippingQuote({
    country: shippingCountry,
    size: limitedEditionSizeKey,
    enabled: isLimitedEditionPurchase && Boolean(shippingCountry) && Boolean(limitedEditionSizeKey),
  });

  const purchaseDetails = getArtworkPurchaseDetails(artworkData);
  const currency = purchaseDetails.currency;
  const artworkPriceRaw = purchaseDetails.price;
  const normalizedArtworkPrice = normalizePriceValue(artworkPriceRaw);
  // Mirror desktop behaviour so pricing reflects the selected limited edition variant.
  const selectedLimitedEditionPrice = normalizePriceValue(
    formState.limited_edition_size?.size_data?.price ?? null,
  );
  const selectedFramePrice = normalizePriceValue(formState.limited_edition_frame_price) ?? 0;
  const selectedMuseumLabelPrice = normalizePriceValue(formState.limited_edition_museum_label_price) ?? 0;
  const limitedEditionSubtotal = (selectedLimitedEditionPrice ?? 0) + selectedFramePrice + selectedMuseumLabelPrice;
  const hasSelectedLimitedEditionSize = Number.isFinite(selectedLimitedEditionPrice);
  const hasSelectedFrameOption = Boolean(formState.limited_edition_frame);
  const hasSelectedMuseumLabelOption = Boolean(formState.limited_edition_museum_label);
  const hasCompleteLimitedEditionSelection = !isLimitedEditionPurchase
    || (hasSelectedLimitedEditionSize && hasSelectedFrameOption && hasSelectedMuseumLabelOption);
  const basePrice = isLimitedEditionPurchase
    ? limitedEditionSubtotal
    : (normalizedArtworkPrice ?? 0);
  const frameSelectionId = formState.limited_edition_frame;
  const museumLabelSelectionId = formState.limited_edition_museum_label;

  const shipCfg = getArtworkShippingConfig(artworkData);
  const ownTier = shipCfg.artwork_shipping_own;
  const restTier = shipCfg.artwork_shipping_rest;
  const exceptions = shipCfg.artwork_shipping_exceptions;

  const shippingCost = (() => {
    if (isLimitedEditionPurchase) {
      if (!limitedEditionSizeKey || !shippingCountry) {
        return null;
      }
      if (!limitedEditionShippingQuote) {
        return null;
      }
      const amount = normalizePriceValue(limitedEditionShippingQuote.amount);
      return Number.isFinite(amount) ? amount : 0;
    }

    if (!formState.shipping_details) return 0;
    const dest = formState.shipping_details.address_country;
    const exception = exceptions.find((entry) => entry.location === dest);
    const exceptionPrice = normalizePriceValue(exception?.price);
    if (exceptionPrice !== null) {
      return exceptionPrice;
    }

    if (dest === ownTier.location) {
      const ownPrice = normalizePriceValue(ownTier.price);
      if (ownPrice !== null) {
        return ownPrice;
      }
    }

    const restPrice = normalizePriceValue(restTier.price);
    return restPrice ?? 0;
  })();
  const shippingCurrency = isLimitedEditionPurchase
    ? (limitedEditionShippingQuote?.currency ?? currency)
    : currency;
  const sellerCountry = isLimitedEditionPurchase
    ? 'Spain'
    : purchaseDetails.galleryCountry;
  const buyerCountry = shippingCountry;
  const billingInfo = formState.billing_info ?? profileBillingInfo ?? null;
  const sellerTaxEnabled = purchaseDetails.galleryTaxEnabled;

  const normalizedServerPricing = useMemo(() => {
    if (!serverPricing) {
      return null;
    }
    const normalizedCurrency = serverPricing.currency || currency;
    const normalizeAmount = (value) => {
      const parsed = normalizePriceValue(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    return {
      price: normalizeAmount(serverPricing.price),
      shipping: normalizeAmount(serverPricing.shipping),
      taxes: normalizeAmount(serverPricing.taxes),
      total: normalizeAmount(serverPricing.total),
      currency: normalizedCurrency,
      vatRate: parseVatRate(serverPricing.vat_rate),
      vatCountry: serverPricing.vat_country ?? null,
      taxMode: serverPricing.tax_mode ?? null,
      taxNoticeCode: serverPricing.tax_notice_code ?? null,
    };
  }, [currency, serverPricing]);

  useEffect(() => {
    setServerPricing(null);
  }, [basePrice, shippingCost, shippingCountry, billingInfo?.vat_id, billingInfo?.vat_valid, billingInfo?.is_business]);

  useEffect(() => {
    setFormState((prevState) => {
      if (prevState.billing_address || prevState.billing_info) {
        return prevState;
      }

      if (!profileBillingAddress && !profileBillingInfo) {
        return prevState;
      }

      return {
        ...prevState,
        billing_address: profileBillingAddress ?? null,
        billing_info: profileBillingInfo ?? null,
      };
    });
  }, [profileBillingAddress, profileBillingInfo]);

  const clientTaxContext = useMemo(() => {
    if (!buyerCountry || !sellerCountry) {
      return { taxMode: null, taxNoticeCode: null };
    }
    if (qualifiesForEuReverseCharge({
      buyerCountry,
      sellerCountry,
      billingInfo,
    })) {
      return {
        taxMode: TAX_MODES.REVERSE_CHARGE,
        taxNoticeCode: "REVERSE_CHARGE_EU_VAT",
      };
    }
    if (isLimitedEditionPurchase) {
      return {
        taxMode: TAX_MODES.PLATFORM_TAX_COLLECTED,
        taxNoticeCode: null,
      };
    }
    return resolveOriginalTaxContext({
      buyerCountry,
      sellerCountry,
      sellerTaxEnabled,
    });
  }, [billingInfo, buyerCountry, isLimitedEditionPurchase, sellerCountry, sellerTaxEnabled]);
  const effectiveTaxMode = normalizedServerPricing?.taxMode ?? clientTaxContext.taxMode;
  const effectiveTaxNoticeCode = normalizedServerPricing?.taxNoticeCode ?? clientTaxContext.taxNoticeCode;
  const isNoTaxMode = effectiveTaxMode === TAX_MODES.NO_TAX_COLLECTED_NOTICE;

  const fallbackVatRate = useMemo(() => {
    if (!buyerCountry || !sellerCountry || effectiveTaxMode === TAX_MODES.REVERSE_CHARGE) {
      return null;
    }
    return resolveVatRate({ buyerCountry, sellerCountry });
  }, [buyerCountry, effectiveTaxMode, sellerCountry]);
  const fallbackVatAmount = useMemo(() => {
    if (!buyerCountry || !sellerCountry || effectiveTaxMode === TAX_MODES.REVERSE_CHARGE) {
      return null;
    }
    return calculateVatAmount({
      price: basePrice,
      shipping: shippingCost ?? 0,
      buyerCountry,
      sellerCountry,
    });
  }, [basePrice, buyerCountry, effectiveTaxMode, sellerCountry, shippingCost]);
  const backendVatAmount = typeof normalizedServerPricing?.taxes === 'number'
    ? normalizedServerPricing.taxes
    : null;
  const backendTotalAmount = typeof normalizedServerPricing?.total === 'number'
    ? normalizedServerPricing.total
    : null;
  const resolvedShippingCost = Number.isFinite(shippingCost) ? shippingCost : 0;
  const fallbackVatCost = fallbackVatAmount ?? 0;
  const fallbackTotalCost = roundMoney(basePrice + resolvedShippingCost + fallbackVatCost) ?? 0;
  const serverCurrency = normalizedServerPricing?.currency ?? null;
  const vatAmountForDisplay = useMemo(() => {
    if (isNoTaxMode) {
      return 0;
    }
    if (backendVatAmount === null || backendVatAmount === undefined) {
      return fallbackVatAmount ?? null;
    }
    if (backendVatAmount <= 0 && typeof fallbackVatAmount === 'number' && fallbackVatAmount > 0) {
      return fallbackVatAmount;
    }
    return backendVatAmount;
  }, [backendVatAmount, fallbackVatAmount, isNoTaxMode]);
  const formattedVat = useMemo(() => {
    if (vatAmountForDisplay === null || vatAmountForDisplay === undefined) {
      return null;
    }
    const safeAmount = vatAmountForDisplay <= 0 ? 0 : vatAmountForDisplay;
    return formatMoney(safeAmount, serverCurrency ?? currency);
  }, [currency, serverCurrency, vatAmountForDisplay]);

  const formattedPrice = isLimitedEditionPurchase
    ? (hasSelectedLimitedEditionSize
      ? formatMoney(selectedLimitedEditionPrice ?? 0, currency)
      : '-')
    : formatMoney(normalizedArtworkPrice ?? 0, currency);
  const formattedShipping = Number.isFinite(shippingCost)
    ? formatMoney(shippingCost, shippingCurrency)
    : null;
  const formattedTotal = useMemo(() => {
    const expectedVat = vatAmountForDisplay ?? fallbackVatAmount ?? 0;
    if (backendTotalAmount !== null) {
      const expectedMinimum = basePrice + resolvedShippingCost + expectedVat;
      const useBackendTotal = backendTotalAmount >= expectedMinimum * 0.999; // allow tiny rounding differences
      if (useBackendTotal) {
        return formatMoney(backendTotalAmount, serverCurrency ?? currency);
      }
    }
    if (isLimitedEditionPurchase && !hasSelectedLimitedEditionSize) {
      return '-';
    }
    return formatMoney(fallbackTotalCost, currency);
  }, [
    backendTotalAmount,
    currency,
    fallbackTotalCost,
    vatAmountForDisplay,
    fallbackVatAmount,
    hasSelectedLimitedEditionSize,
    isLimitedEditionPurchase,
    serverCurrency,
    resolvedShippingCost,
    basePrice,
  ]);
  const selectionStepTotalDisplay = useMemo(() => {
    if (normalizedServerPricing && typeof normalizedServerPricing.price === 'number') {
      const vatAmount = typeof normalizedServerPricing.taxes === 'number'
        ? normalizedServerPricing.taxes
        : 0;
      return formatMoney(normalizedServerPricing.price + vatAmount, serverCurrency ?? currency);
    }
    if (!isLimitedEditionPurchase) {
      return formatMoney(basePrice + fallbackVatCost, currency);
    }
    if (!hasSelectedLimitedEditionSize) {
      return '-';
    }
    return formatMoney(basePrice + fallbackVatCost, currency);
  }, [
    basePrice,
    currency,
    fallbackVatCost,
    hasSelectedLimitedEditionSize,
    isLimitedEditionPurchase,
    normalizedServerPricing,
    serverCurrency,
  ]);
  const serverPriceAmount = typeof normalizedServerPricing?.price === 'number'
    ? normalizedServerPricing.price
    : null;
  const paymentPriceDisplay = useMemo(() => {
    if (!isLimitedEditionPurchase && serverPriceAmount !== null) {
      return formatMoney(serverPriceAmount, serverCurrency ?? currency);
    }
    return formattedPrice;
  }, [
    currency,
    formattedPrice,
    isLimitedEditionPurchase,
    serverCurrency,
    serverPriceAmount,
  ]);
  const backendVatRate = typeof normalizedServerPricing?.vatRate === 'number'
    ? normalizedServerPricing.vatRate
    : null;
  const effectiveVatRate = useMemo(() => {
    if (isNoTaxMode) {
      return 0;
    }
    if (typeof backendVatRate === 'number' && backendVatRate > 0) {
      return backendVatRate;
    }
    if (typeof fallbackVatRate === 'number' && fallbackVatRate > 0) {
      return fallbackVatRate;
    }
    return backendVatRate ?? fallbackVatRate ?? null;
  }, [backendVatRate, fallbackVatRate, isNoTaxMode]);
  const vatPercentageLabel = typeof effectiveVatRate === 'number'
    ? formatVatRate(effectiveVatRate)
    : null;
  const taxLabelBase = isLimitedEditionPurchase ? 'VAT' : 'Tax';
  const vatLabel = vatPercentageLabel ? `${taxLabelBase} (${vatPercentageLabel})` : taxLabelBase;
  const shouldShowZeroVatInfo = effectiveVatRate === 0 && vatAmountForDisplay !== null;
  const taxNoticeMessage = effectiveTaxNoticeCode
    ? (TAX_NOTICE_COPY[effectiveTaxNoticeCode] ?? null)
    : null;
  const vatInfoMessage = taxNoticeMessage ?? (shouldShowZeroVatInfo ? ZERO_VAT_TOOLTIP : null);
  if (!artworkData) {
    return (
      <div className="buy-details-page__container">
        <p>No artwork data provided. Please return and try again.</p>
      </div>
    );
  }
  const galleryProfile = getArtworkPurchaseGalleryProfile(artworkData);
  const galleryUsername = galleryProfile.username;

  const navigateToProfile = (username) => {
    if (!username) {
      return;
    }

    if (artworkData?.slug) {
      trackArtworkPreferenceInteraction({
        slug: artworkData.slug,
        signal: ARTWORK_PREFERENCE_SIGNALS.GALLERY_PROFILE_VIEW_FROM_ARTWORK,
        metadata: { source: 'shipping-gallery-info-container' },
      });
    }

    navigate(`/user/${username}`);
  };

  const handleProfileKeyDown = (username) => (event) => {
    if (!username) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateToProfile(username);
    }
  };

  async function continueToCheckout() {
    if (formLoading) {
      return;
    }

    setFormLoading(true);
    setFormError(null);
    try {
      const taxesAmount = Math.max(0, vatAmountForDisplay ?? fallbackVatAmount ?? 0);
      const shippingAmount = Number.isFinite(resolvedShippingCost) ? resolvedShippingCost : 0;
      const pricingPayload = {
        price: basePrice,
        shipping: shippingAmount,
        taxes: taxesAmount,
        total: basePrice + shippingAmount + taxesAmount,
        currency: serverCurrency ?? currency,
        shipping_currency: shippingCurrency ?? currency,
        vat_rate: effectiveVatRate ?? null,
        vat_country: buyerCountry ?? null,
        source: 'client',
      };
      const purchasePayload = {
        ...formState,
        pricing: pricingPayload,
        client_pricing: pricingPayload,
      };
      const { response, data } = await UserService.createArtworkPaymentIntent(
        artworkData._id,
        purchasePayload,
        typeOfPurchase
      );

      if (!response?.ok) {
        const errorCode = data?.error_type ?? data?.code ?? "PAYMENT_FAILED";
        if (PROFILE_ERROR_CODES.has(errorCode)) {
          await promptProfileUpdate(errorCode);
        }
        else {
          setFormError({ element: 'purchase_error', error_code: errorCode });
        }
        return;
      }

      setServerPricing(data?.pricing ?? null);
      await handlePaymentIntentResponse(data);
    }
    catch (error) {
      console.error("[PurchaseProcess] Checkout failed", error);
      setFormError({ element: 'purchase_error', error_code: error?.code ?? 'PAYMENT_FAILED' });
    }
    finally {
      setFormLoading(false);
    }
  }

  const hasAddress = Boolean(formState.shipping_details);
  const shippingReady = isLimitedEditionPurchase
    ? (hasAddress && Boolean(limitedEditionSizeKey) && Boolean(limitedEditionShippingQuote))
    : hasAddress;
  const fallbackShippingDisplay = shippingReady && formattedShipping ? formattedShipping : '-';
  const shippingAmountFromServer = typeof normalizedServerPricing?.shipping === 'number'
    ? normalizedServerPricing.shipping
    : null;
  const shippingDisplay = normalizedServerPricing
    ? (
      shippingReady && Number.isFinite(shippingAmountFromServer ?? resolvedShippingCost)
        ? formatMoney(shippingAmountFromServer ?? resolvedShippingCost, serverCurrency ?? currency)
        : '-'
    )
    : fallbackShippingDisplay;
  const shouldShowTotal = hasCompleteLimitedEditionSelection && shippingReady;
  const totalDisplay = shouldShowTotal ? formattedTotal : '-';
  const isLimitedEditionSelectionStep = isLimitedEditionPurchase && currentBuySection === 'size';
  const continueFromSelection = () => {
    if (!isLimitedEditionSelectionStep || !hasCompleteLimitedEditionSelection) {
      return;
    }

    setCurrentBuySection('shipping');
  };
  const footerButtonLabel = isLimitedEditionSelectionStep
    ? t('buy_details.footer.continue')
    : t('buy_details.footer.pay_now');
  const footerButtonTurnedOff = isLimitedEditionSelectionStep
    ? !hasCompleteLimitedEditionSelection
    : (!shippingReady || !hasCompleteLimitedEditionSelection);
  const footerButtonHandler = isLimitedEditionSelectionStep ? continueFromSelection : continueToCheckout;
  const footerTotalDisplay = isLimitedEditionSelectionStep ? selectionStepTotalDisplay : totalDisplay;
  const frameDetails = isLimitedEditionPurchase
    && frameSelectionId
    && frameSelectionId !== 'none'
    && hasSelectedFrameOption
    && selectedFramePrice > 0
    ? {
      label: `Frame (${FRAME_OPTION_LABELS[frameSelectionId] ?? frameSelectionId})`,
      value: `+${NumberUtils.getArtworkPrice(selectedFramePrice, currency)}`,
    }
    : null;
  const museumLabelDetails = isLimitedEditionPurchase
    && museumLabelSelectionId
    && museumLabelSelectionId !== 'none'
    && hasSelectedMuseumLabelOption
    && selectedMuseumLabelPrice > 0
    ? {
      label: `Museum Label (${MUSEUM_LABEL_OPTION_LABELS[museumLabelSelectionId] ?? museumLabelSelectionId})`,
      value: `+${NumberUtils.getArtworkPrice(selectedMuseumLabelPrice, currency)}`,
    }
    : null;

  return (
    <div className="buy-details-page__container">
      <header className="buy-details-page__header">
        <BackArrowIcon
          className="buy-details-page__back-arrow"
          style={{ fill: 'var(--app-text-color)' }}
          onClick={goBackHandler}
        />
      </header>

      <main className="buy-details-page__content">
        <div className="buy-details-page__hero">
          <ArtworkImage
            image={artworkData.artwork_media.artwork_main_picture.image_id}
            imageClassName="buy-details-page__artwork-image"
            imageTemplateClassName="buy-details-page__artwork-image"
          />

          <ArtworkLabel
            artworkData={artworkData}
            hideSocials
          />
        </div>
        <div className="buy-details-page__sections">
          {currentBuySection === 'size' ? (
            <SelectSize
              artworkData={artworkData}
              formState={formState}
              setFormState={setFormState}
              typeOfPurchase={typeOfPurchase}
            />
          ) : (
            <>
              {/* {!isLimitedEditionPurchase && (
                <hr className="purchase-shipping-details__divider" />
              )} */}

              <ShippingDetails
                artworkData={artworkData}
                formState={formState}
                setFormState={setFormState}
                typeOfPurchase={typeOfPurchase}
              />

              <div className="shipping-gallery-info-container">
                <UserProfileImage
                  image={galleryProfile.imageId}
                  typeOfProfile="gallery"
                  className="shipping-gallery-image"
                  onClick={isLimitedEditionPurchase ? undefined : () => navigateToProfile(galleryUsername)}
                />
                <div className="shipping-gallery-info">
                  <p
                    className="shipping-gallery-name"
                    role={isLimitedEditionPurchase ? undefined : 'button'}
                    tabIndex={isLimitedEditionPurchase ? undefined : 0}
                    onClick={isLimitedEditionPurchase ? undefined : () => navigateToProfile(galleryUsername)}
                    onKeyDown={isLimitedEditionPurchase ? undefined : handleProfileKeyDown(galleryUsername)}
                  >
                    {isLimitedEditionPurchase
                      ? t('buy_details.shipping.suarte_name')
                      : galleryProfile.name}
                  </p>
                  <p className="shipping-from">
                    {isLimitedEditionPurchase
                      ? t('buy_details.shipping.ships_from_alicante')
                      : t('buy_details.shipping.ships_from_city_country', {
                        city: galleryProfile.city,
                        country: galleryProfile.country,
                      })}
                  </p>
                  <p className="shipping-timeline">
                    {isLimitedEditionPurchase
                      ? t('buy_details.shipping.timeline_limited')
                      : t('buy_details.shipping.timeline_standard')}
                  </p>
                </div>
              </div>

              <hr className="purchase-shipping-details__divider" />

              <PurchasePaymentMethodPanel onManagePaymentMethods={handleManagePaymentMethods} />

              <hr className="purchase-shipping-details__divider" />

              <BillingDetailsCard
                billingAddress={formState.billing_address}
                billingInfo={formState.billing_info}
                shippingAddress={formState.shipping_details}
                onSaved={({ billingAddress, billingInfo }) => {
                  setFormState((prevState) => ({
                    ...prevState,
                    billing_address: billingAddress,
                    billing_info: billingInfo,
                  }));
                }}
              />

              <hr className="purchase-shipping-details__divider" />

              <PaymentDetails
                price={paymentPriceDisplay}
                frameDetails={frameDetails}
                museumLabelDetails={museumLabelDetails}
                shipping={shippingDisplay}
                vat={formattedVat}
                vatLabel={vatLabel}
                vatInfo={vatInfoMessage}
                total={totalDisplay}
              />

              <PersonalizedMessage
                formState={formState}
                setFormState={setFormState}
                typeOfPurchase={typeOfPurchase}
                artworkData={artworkData}
              />

              <FormError element="purchase_error" error={formError} />

              <ContinueButton
                loading={formLoading}
                turnOff={!shippingReady || !hasCompleteLimitedEditionSelection}
                className="purchase-shipping-details__submit font-size-medium"
                onClick={continueToCheckout}
              >
                {t('buy_details.footer.pay_now')}
              </ContinueButton>

              <hr className="purchase-shipping-details__divider" />

              <div className="purchase-guarantee">
                <h3 className="purchase-protection__heading">{t('buy_details.guarantee.heading')}</h3>
                <div className="guarantee__row">
                  <span className="guarantee__text">{t('buy_details.guarantee.secure_checkout')}</span>
                </div>
                <div className="guarantee__row">
                  <span className="guarantee__text">
                    {t('buy_details.guarantee.cancellation_policy')}
                  </span>
                </div>
                <div className="guarantee__row">
                  <span className="guarantee__text">{t('buy_details.guarantee.authenticity')}</span>
                </div>
                <a href={resolveLocalizedPublicPath('/buyer-protection')} className="guarantee__learn-more">
                  {t('buy_details.guarantee.learn_more')}
                </a>
              </div>

              <hr className="purchase-shipping-details__divider" />

              <div className="purchase-support">
                <h3 className="purchase-support__heading">{t('buy_details.support.heading')}</h3>
                <p className="support__row">
                  {`${t('buy_details.support.call_or_whatsapp')} `}
                  <a href="tel:+346281714101" className="support__link">
                    +34 628 171 410
                  </a>
                </p>
                <p className="support__row">
                  {`${t('buy_details.support.send_email')} `}
                  <a href="mailto:contact@suarte.art" className="support__link">
                    contact@suarte.art
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      <div className="purchase-footer">
        <input type="hidden" id="purchase_error" />
        <div className="purchase-footer__total">
          <span className="purchase-footer__total-label">{t('purchase_footer.total_label')}</span>
          <span className="purchase-footer__total-value">{footerTotalDisplay}</span>
        </div>
        <ContinueButton
          loading={formLoading}
          turnOff={footerButtonTurnedOff}
          className="purchase-footer__button"
          onClick={footerButtonHandler}
        >
          {footerButtonLabel}
        </ContinueButton>
      </div>

      <PopupCertificateOfAuthenticity
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
      <PopupCertificateOfOwnership
        open={ownershipModalOpen}
        onClose={() => setOwnershipModalOpen(false)}
      />
      {purchasePayment && (
        <PurchasePaymentModal
          clientSecret={purchasePayment.clientSecret}
          stripe={purchasePayment.stripe}
          title={t('buy_details.footer.pay_now')}
          submitLabel={t('buy_details.footer.pay_now')}
          onCancel={dismissPurchasePayment}
          onComplete={completePurchasePayment}
        />
      )}
    </div>
  );
}
